import React, { ReactNode, useCallback, useRef, useState } from "react";
import styled, { css } from "styled-components";

import { useOnMount } from "../../hooks/useOnMount";

const ScrolledAreaDiv = styled.div<{ $hasScrolled?: boolean }>`
  width: 100%;
  height: 100%;
  overflow: auto;
  border-top: 1px solid transparent;
  transition: border-color
    ${({ theme }) => `${theme.animation.duration} ${theme.animation.easing}`};

  ${({ $hasScrolled, theme }) =>
    $hasScrolled &&
    css`
      border-top: 1px solid ${theme.borderColor.MUTED};
    `}
`;

export interface ScrolledAreaProps {
  readonly children: ReactNode;
  readonly id?: string;
  readonly className?: string;
}

export const ScrolledArea: React.FunctionComponent<ScrolledAreaProps> = ({
  children,
  className,
  id,
}) => {
  // Scroll detection
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState<number | undefined>(0);
  const handleScroll = useCallback(() => {
    const position = scrollRef.current?.scrollTop;
    setScrollPosition(position);
  }, []);

  useOnMount(() => {
    const scrollDiv = scrollRef.current;
    scrollDiv?.addEventListener("scroll", handleScroll, {
      passive: true,
    });
    return () => {
      scrollDiv?.removeEventListener("scroll", handleScroll);
    };
  });

  return (
    <ScrolledAreaDiv
      ref={scrollRef}
      $hasScrolled={scrollPosition !== undefined && scrollPosition > 0}
      className={className}
      id={id}
    >
      {children}
    </ScrolledAreaDiv>
  );
};
